import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, Twitter, Mail, Phone, MapPin } from "lucide-react";
import BrandLogo from "../common/BrandLogo";
import { useI18n } from "../../lib/i18n";
import { LEGAL_DOCS, LEGAL_LINKS } from "../../lib/legal";

const ICONS = { linkedin: Linkedin, instagram: Instagram, youtube: Youtube, twitter: Twitter };

const Footer = ({ settings }) => {
  const { t, pick, lang } = useI18n();
  const s = settings || {};
  const year = new Date().getFullYear();
  const copy = (s.copyright_text || "© {year} Fırat Construction Yapı A.Ş.").replace("{year}", year);

  return (
    <footer className="bg-brand-navy text-white relative overflow-hidden" data-testid="site-footer">
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="container-x py-20 md:py-28 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" aria-label="Ana sayfa" className="inline-flex items-center">
              <BrandLogo place="footer" testId="footer-logo" />
            </Link>
            <p className="mt-8 max-w-md text-white/70 leading-relaxed">
              {pick(s.footer_tagline, "Building architectural legacies that endure for generations.")}
            </p>
            <div className="mt-10 flex items-center gap-4">
              {(s.socials || []).map((so) => {
                const Icon = ICONS[so.platform] || Mail;
                return (
                  <a
                    key={so.platform}
                    href={so.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={so.platform}
                    className="w-10 h-10 border border-white/25 flex items-center justify-center hover:bg-brand-gold hover:border-brand-gold transition-colors"
                    data-testid={`footer-social-${so.platform}`}
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-white/75">{lang === "tr" ? "Keşfet" : "Explore"}</div>
            <ul className="mt-6 space-y-3 text-sm">
              {["about", "businesses", "projects", "media", "careers", "contact"].map((k) => (
                <li key={k}>
                  <Link
                    to={`/${k === "about" ? "about" : k}`}
                    className="text-white/80 hover:text-brand-gold transition-colors"
                    data-testid={`footer-link-${k}`}
                  >
                    {t(`nav.${k}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow text-white/75">{lang === "tr" ? "Genel Merkez" : "Headquarters"}</div>
            <ul className="mt-6 space-y-4 text-sm text-white/80">
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.5} className="mt-0.5 text-brand-gold" />
                <span>{pick(s.hq_address, "")}</span>
              </li>
              {s.contact_phone ? (
                <li className="flex items-center gap-3">
                  <Phone size={16} strokeWidth={1.5} className="text-brand-gold" />
                  <a href={`tel:${s.contact_phone}`} className="hover:text-brand-gold transition-colors">
                    {s.contact_phone}
                  </a>
                </li>
              ) : null}
              {s.contact_email ? (
                <li className="flex items-center gap-3">
                  <Mail size={16} strokeWidth={1.5} className="text-brand-gold" />
                  <a href={`mailto:${s.contact_email}`} className="hover:text-brand-gold transition-colors">
                    {s.contact_email}
                  </a>
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/50">
          <div data-testid="footer-copyright">{copy}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {LEGAL_LINKS.map((l) => {
              const d = LEGAL_DOCS[l.slug];
              return (
                <Link
                  key={l.slug}
                  to={`/legal/${l.slug}`}
                  className="text-white/70 hover:text-white transition-colors tracking-[0.16em] uppercase text-[0.62rem]"
                  data-testid={`footer-legal-${l.slug}`}
                >
                  {pick(d.title)}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("firat:open-cookie-prefs"))}
              className="text-white/70 hover:text-white transition-colors tracking-[0.16em] uppercase text-[0.62rem]"
              data-testid="footer-cookie-prefs"
            >
              Çerez Tercihleri
            </button>
            <span className="text-white/55 tracking-[0.22em] uppercase text-[0.6rem]">Est. 1984 · Istanbul · Dubai</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
